import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'
import { FaRProject, FaTimes, FaChevronDown, FaFilePdf, FaFileExcel, FaImage } from 'react-icons/fa';
import { CgMenuRight } from 'react-icons/cg';
import { IconContext } from 'react-icons';
import logo from '../../assets/logo.png';
import LanguageSelect from '../Languages/LanguageSelect';
import {
	Nav,
	NavbarContainer,
	NavLogo,
	NavIcon,
	MobileIcon,
	NavMenu,
	NavLinks,
	NavItem,
	DropdownContainer,
	DropdownMenu,
	DropdownItem,
	LanguageSelectWrapper,
} from './NavbarStyles.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { data } from '../../data/NavbarData';

const Navbar = ({color}) => {
	const { t } = useTranslation();
	const [show, setShow] = useState(false);
	const [activeDropdown, setActiveDropdown] = useState(null);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 960);
	const dropdownRefs = useRef([]);

	const colortitle = show ? 'white' : 'black';

	const getFileIcon = (type) => {
		if (!type) return null;
		switch (type.toLowerCase()) {
			case 'pdf':
				return <FaFilePdf style={{ marginRight: '0.5rem' }} />;
			case 'excel':
				return <FaFileExcel style={{ marginRight: '0.5rem' }} />;
			case 'image':
				return <FaImage style={{ marginRight: '0.5rem' }} />;
			default:
				return null;
		}
	};

	let navigate = useNavigate();
	let location = useLocation();
	const isHomePage = location.pathname === '/';

	const handleClick = () => {
		setShow(!show);
	};

	const scrollTo = (id) => {
		const element = document.getElementById(id);

		element.scrollIntoView({
			behavior: 'smooth',
		});
	};

	const closeMobileMenu = (to, id) => {
		if (id && location.pathname === '/') {
			scrollTo(id)
			  
		}

		navigate(to);
		setShow(false);
		setActiveDropdown(null);
	};

	const handleDropdownToggle = (index, event) => {
		event.stopPropagation();
		setActiveDropdown(activeDropdown === index ? null : index);
	};

	const handleDropdownItemClick = (to, isDownload = false) => {
		if (isDownload) {
			// Para archivos, abrir en nueva pestaña
			window.open(to, '_blank');
		} else {
			navigate(to);
		}
		setShow(false);
		setActiveDropdown(null);
	};

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 960);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);
	
	// Cerrar dropdown al hacer click fuera (solo en desktop)
	useEffect(() => {
		if (isMobile || activeDropdown === null) return;

		const handleClickOutside = (event) => {
			const clickedOutside = dropdownRefs.current.every(ref => 
				ref && !ref.contains(event.target)
			);
			
			if (clickedOutside) {
				setActiveDropdown(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [activeDropdown, isMobile]);

	const [scrollNav, setScrollNav] = useState(false);

	const changeNav = () => {
	  if (window.scrollY >= 80) {
		setScrollNav(true);
	  } else {
		setScrollNav(false);
	  }
	};
  
	useEffect(() => {
	  window.addEventListener('scroll', changeNav);
	}, []);

	return (
		<IconContext.Provider value={{ color: 'white'}}>
			<Nav scrollNav={scrollNav} isHomePage={isHomePage}>
				<NavbarContainer >
					<NavLogo to="/">
						<img src={logo} alt="Radixagro semillas" height={44} style={{ width: 'auto', display: 'block' }} />
					</NavLogo>
					<MobileIcon onClick={handleClick}>
						{show ? <FaTimes /> : <CgMenuRight style={{fill : scrollNav ? 'black' : 'white'}} />}
					</MobileIcon>
					<NavMenu show={show} >
						{data.map((el, index) => (
							<React.Fragment key={index}>
								<NavItem>
									{el.hasDropdown ? (
										isMobile ? (
											// Mobile: Accordion behavior
											<>
												<NavLinks
													color={color}
													onClick={(e) => handleDropdownToggle(index, e)}
													style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
												>
													{t(`navbar_data.${index}.text`)}
													<FaChevronDown 
														size={12} 
														style={{ 
															transform: activeDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
															transition: 'transform 0.3s ease'
														}}
													/>
												</NavLinks>
											</>
										) : (
											// Desktop: Dropdown behavior
											<DropdownContainer ref={el => dropdownRefs.current[index] = el}>
												<NavLinks
													color={color}
													onClick={(e) => handleDropdownToggle(index, e)}
													style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
												>
													{t(`navbar_data.${index}.text`)}
													<FaChevronDown 
														size={12} 
														style={{ 
															transform: activeDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
															transition: 'transform 0.3s ease'
														}}
													/>
												</NavLinks>
												<DropdownMenu show={activeDropdown === index}>
													{el.dropdownItems.map((item, itemIndex) => (
														<DropdownItem 
															key={itemIndex}
															onClick={() => handleDropdownItemClick(item.to, item.isDownload)}
														>
															{getFileIcon(item.type)}
															{item.text}
														</DropdownItem>
													))}
												</DropdownMenu>
											</DropdownContainer>
										)
									) : (
										<NavLinks
											color={color}
											onClick={() => closeMobileMenu(el.to, el.id)}
										>
											{t(`navbar_data.${index}.text`)}
										</NavLinks>
									)}
								</NavItem>
								{/* Mobile accordion items */}
								{el.hasDropdown && isMobile && activeDropdown === index && (
									el.dropdownItems.map((item, itemIndex) => (
										<NavItem key={`dropdown-${index}-${itemIndex}`} isDropdownItem>
											<NavLinks
												color={color}
												onClick={() => handleDropdownItemClick(item.to, item.isDownload)}
												style={{ paddingLeft: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
											>
												{getFileIcon(item.type)}
												{item.text}
											</NavLinks>
										</NavItem>
									))
								)}
							</React.Fragment>
						))}
						<LanguageSelectWrapper>
							<LanguageSelect close={closeMobileMenu}/>
						</LanguageSelectWrapper>
					</NavMenu>
				</NavbarContainer>
			</Nav>
		</IconContext.Provider>
	);
};

export default Navbar;